import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  FolderOpen, PenTool, Type as TypeIcon, Shapes, Sparkles, Palette, LayoutTemplate,
  Layers, ClipboardCheck, ArrowLeft, Loader2, ChevronsUp, ChevronsDown, Image as ImageIcon,
  Trash2, Copy, ChevronUp, ChevronDown, Wand2, Eraser, Maximize2, LayoutGrid, Camera,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

import { EditorToolSidebar } from '@/components/editor/EditorToolSidebar';
import { EditorMediaPanel } from '@/components/editor/EditorMediaPanel';
import { EditorBrandKitPanel } from '@/components/editor/EditorBrandKitPanel';
import { EditorTemplatesPanel } from '@/components/editor/EditorTemplatesPanel';
import { DesignCanvas } from '@/components/editor/DesignCanvas';
import { DesignElementProperties } from '@/components/editor/DesignElementProperties';
import { DesignLayersPages } from '@/components/editor/DesignLayersPages';
import { DesignCreatePanel } from '@/components/editor/DesignCreatePanel';
import { DesignElementsPalette } from '@/components/editor/DesignElementsPalette';
import { DesignTextPanel } from '@/components/editor/DesignTextPanel';
import { DesignReviewPanel } from '@/components/editor/DesignReviewPanel';
import { ThumbnailSourcePanel } from '@/components/editor/ThumbnailSourcePanel';
import { ThumbnailCaptureStage } from '@/components/editor/ThumbnailCaptureStage';
import { assetTypeLabel, platformLabel, aspectForAssetType } from '@/lib/clipPlatformFormats';
import { getThumbnailCandidatesForSource, getDefaultThumbnailMap, setDefaultThumbnail as persistDefaultThumbnail, sourceKeyFor } from '@/lib/thumbnailAssociations';
import { renderPageToBlob } from '@/lib/designRaster';
import { ProductionToolsDropdown } from '@/components/production/ProductionToolsDropdown';

const API_URL = window.__BACKEND_URL;
const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const DESIGN_TOOL_CATEGORIES = [
  { key: 'media', label: 'Media', icon: FolderOpen },
  { key: 'design', label: 'Design', icon: PenTool },
  { key: 'thumbnails', label: 'Thumbnails', icon: Camera },
  { key: 'text', label: 'Text', icon: TypeIcon },
  { key: 'elements', label: 'Elements', icon: Shapes },
  { key: 'ai', label: 'AI Tools', icon: Sparkles },
  { key: 'brand', label: 'Brand Kit', icon: Palette },
  { key: 'templates', label: 'Templates', icon: LayoutTemplate },
  { key: 'layers', label: 'Layers & Pages', icon: Layers },
  { key: 'review', label: 'Review', icon: ClipboardCheck },
];
const CATEGORY_LABELS = Object.fromEntries(DESIGN_TOOL_CATEGORIES.map((c) => [c.key, c.label]));
const MULTI_PAGE_TYPES = ['carousel', 'multi_image_story'];

const isImageAsset = (a) => {
  const key = (a?.input_file_url || '').toLowerCase();
  return ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.heic', '.heif'].some((ext) => key.endsWith(ext));
};

const AI_CAPABILITIES = [
  { key: 'bg', icon: Wand2, label: 'Generate Background', desc: 'Describe a scene and fill the page background with it.' },
  { key: 'removebg', icon: Eraser, label: 'Remove Background', desc: 'Cut an uploaded image out from its background automatically.' },
  { key: 'resize', icon: Maximize2, label: 'Smart Resize', desc: "Reflow this design's layout for a new aspect ratio." },
  { key: 'layout', icon: LayoutGrid, label: 'Auto-Layout Suggestions', desc: 'Suggest a few alternate arrangements for the current page.' },
];

export default function ImagesAndPosts() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const returnTo = `/app/create/workspace/project/${projectId}`;

  const [project, setProject] = useState(null);
  const [parentAssets, setParentAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [brandkits, setBrandkits] = useState([]);

  // ── Legacy thumbnail-setting, preserved from the original gallery page ──
  const [thumbMap, setThumbMap] = useState({});
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputRef = useRef(null);
  const pendingThumbTargetRef = useRef(null);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    fetch(`${API_URL}/api/projects/${projectId}`, { credentials: 'include' })
      .then((res) => { if (res.status === 404) { setNotFound(true); return null; } if (!res.ok) throw new Error('not_found'); return res.json(); })
      .then(async (data) => {
        if (!data) return;
        setProject(data.project);
        const assets = data.parent_assets || [];
        setParentAssets(assets);
        const images = assets.filter(isImageAsset);
        const thumbs = await Promise.all(images.map(async (a) => {
          try {
            const tRes = await fetch(`${API_URL}/api/admin/content-sources/${a.id}/thumbnail`, { credentials: 'include' });
            if (!tRes.ok) return [a.id, null];
            const tData = await tRes.json();
            return [a.id, tData.thumbnail_url || null];
          } catch { return [a.id, null]; }
        }));
        setThumbMap(Object.fromEntries(thumbs));
      })
      .catch(() => toast.error('Could not load this project'))
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => {
    const creatorId = project?.creator_id;
    if (!creatorId) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/brandkits/creator/${creatorId}`, { credentials: 'include' });
        const data = await res.json();
        let kits = data.brand_kits || data.brandkits || [];
        if (kits.length === 0) {
          const appRes = await fetch(`${API_URL}/api/brandkits/app`, { credentials: 'include' });
          const appData = await appRes.json();
          kits = appData.brand_kits || appData.brandkits || [];
        }
        setBrandkits(kits);
      } catch { /* brand kits optional */ }
    })();
  }, [project?.creator_id]);

  const triggerSetThumbnail = (assetId) => { pendingThumbTargetRef.current = assetId; fileInputRef.current?.click(); };
  const handleThumbFileChange = async (e) => {
    const file = e.target.files?.[0];
    const assetId = pendingThumbTargetRef.current;
    e.target.value = '';
    if (!file || !assetId) return;
    setUploadingId(assetId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/api/admin/content-sources/${assetId}/thumbnail`, { method: 'POST', credentials: 'include', body: formData });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Failed to set thumbnail'); }
      const thumbRes = await fetch(`${API_URL}/api/admin/content-sources/${assetId}/thumbnail`, { credentials: 'include' });
      const thumbData = thumbRes.ok ? await thumbRes.json() : { thumbnail_url: null };
      setThumbMap((prev) => ({ ...prev, [assetId]: thumbData.thumbnail_url || null }));
      toast.success('Thumbnail updated');
    } catch (err) { toast.error(err.message || 'Failed to set thumbnail'); }
    finally { setUploadingId(null); }
  };
  const imageAssets = parentAssets.filter(isImageAsset);


  // ── Designs — Level 1/2 local state, persisted per-project in this
  // browser (no backend "image post" model exists yet). ──
  const designsKey = projectId ? `consistency_images_posts_${projectId}` : null;
  const [designs, setDesigns] = useState([]);
  useEffect(() => {
    if (!designsKey) return;
    try { setDesigns(JSON.parse(localStorage.getItem(designsKey) || '[]')); } catch { setDesigns([]); }
  }, [designsKey]);
  const persistDesigns = (next) => {
    setDesigns(next);
    if (designsKey) { try { localStorage.setItem(designsKey, JSON.stringify(next)); } catch { /* storage full/unavailable */ } }
  };

  const [activeDesignId, setActiveDesignId] = useState(null);
  const [activeVariantId, setActiveVariantId] = useState(null);
  const [activePageId, setActivePageId] = useState(null);
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [pendingCreate, setPendingCreate] = useState(null);

  const activeDesign = designs.find((d) => d.id === activeDesignId) || null;
  const activeVariant = activeDesign?.variants.find((v) => v.id === activeVariantId) || null;
  const activePage = activeVariant?.pages.find((p) => p.id === activePageId) || null;
  const selectedElement = activePage?.elements.find((e) => e.id === selectedElementId) || null;
  const multiPage = activeDesign ? MULTI_PAGE_TYPES.includes(activeDesign.assetType) : false;

  const updateDesign = useCallback((designId, partial) => {
    persistDesigns(designs.map((d) => (d.id === designId ? { ...d, ...partial } : d)));
  }, [designs]);
  const updateVariant = (partial) => {
    if (!activeDesign || !activeVariant) return;
    updateDesign(activeDesign.id, { variants: activeDesign.variants.map((v) => (v.id === activeVariant.id ? { ...v, ...partial } : v)) });
  };
  const updatePage = (partial) => {
    if (!activeVariant || !activePage) return;
    updateVariant({ pages: activeVariant.pages.map((p) => (p.id === activePage.id ? { ...p, ...partial } : p)) });
  };

  // ── Thumbnails (section 3/4/6/7) ──
  const [thumbSourceId, setThumbSourceId] = useState(null);
  const [thumbClipId, setThumbClipId] = useState(null); // null = whole video
  const [thumbClips, setThumbClips] = useState([]);
  const [thumbClipsLoading, setThumbClipsLoading] = useState(false);
  const [thumbStreamUrl, setThumbStreamUrl] = useState(null);
  const [thumbStreamError, setThumbStreamError] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [capturedFrameUrl, setCapturedFrameUrl] = useState(null);
  const [captureError, setCaptureError] = useState(null);
  const [thumbCurrentTime, setThumbCurrentTime] = useState(0);
  const [thumbDuration, setThumbDuration] = useState(0);
  const [thumbIsPlaying, setThumbIsPlaying] = useState(false);
  const thumbVideoRef = useRef(null);
  const [thumbDefaultsVersion, setThumbDefaultsVersion] = useState(0); // bump to re-read the defaults map after a write

  const selectThumbSource = (sourceId) => {
    setThumbSourceId(sourceId); setThumbClipId(null); setCapturing(false); setCapturedFrameUrl(null); setCaptureError(null);
    setThumbClipsLoading(true);
    fetch(`${API_URL}/api/editor/projects/source/${sourceId}`, { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => { setThumbClips(data.project?.clips || []); setThumbStreamUrl(data.stream_url || null); setThumbStreamError(data.stream_error || null); })
      .catch(() => toast.error('Could not load this video'))
      .finally(() => setThumbClipsLoading(false));
  };
  const selectThumbClip = (clipId) => { setThumbClipId(clipId); setCapturing(false); setCapturedFrameUrl(null); setCaptureError(null); };
  const thumbClipRange = thumbClipId ? (() => { const c = thumbClips.find((cc) => cc.id === thumbClipId); return c ? { start: c.in_point, end: c.out_point } : null; })() : null;

  const startCapture = () => { setCapturing(true); setCapturedFrameUrl(null); setCaptureError(null); };
  useEffect(() => {
    if (!capturing || !thumbVideoRef.current || !thumbClipRange) return;
    const v = thumbVideoRef.current;
    const seek = () => { v.currentTime = thumbClipRange.start; };
    if (v.readyState >= 1) seek(); else v.addEventListener('loadedmetadata', seek, { once: true });
  }, [capturing, thumbClipId, thumbStreamUrl]);

  const thumbTogglePlay = () => { const v = thumbVideoRef.current; if (!v) return; if (v.paused) v.play(); else v.pause(); };
  const thumbSeek = (t) => { const v = thumbVideoRef.current; if (!v) return; v.currentTime = t; setThumbCurrentTime(t); };
  const thumbStepFrame = (dir) => { const v = thumbVideoRef.current; if (!v) return; const next = Math.max(0, Math.min(v.duration || 0, v.currentTime + dir * (1 / 30))); v.currentTime = next; setThumbCurrentTime(next); };
  useEffect(() => {
    const v = thumbVideoRef.current;
    if (!v || !capturing) return;
    const onMeta = () => setThumbDuration(v.duration || 0);
    const onTime = () => setThumbCurrentTime(v.currentTime);
    const onPlay = () => setThumbIsPlaying(true);
    const onPause = () => setThumbIsPlaying(false);
    v.addEventListener('loadedmetadata', onMeta); v.addEventListener('timeupdate', onTime);
    v.addEventListener('play', onPlay); v.addEventListener('pause', onPause);
    return () => { v.removeEventListener('loadedmetadata', onMeta); v.removeEventListener('timeupdate', onTime); v.removeEventListener('play', onPlay); v.removeEventListener('pause', onPause); };
  }, [capturing, thumbStreamUrl]);

  const handleFrameCaptured = (dataUrl, errorMsg) => {
    if (!dataUrl) { setCaptureError(errorMsg); return; }
    setCapturedFrameUrl(dataUrl); setCaptureError(null);
  };
  const retakeCapture = () => { setCapturedFrameUrl(null); setCaptureError(null); };

  const thumbSourceAsset = parentAssets.find((a) => a.id === thumbSourceId);
  const thumbSourceKind = thumbClipId ? 'clip' : 'video';
  const thumbSourceRefId = thumbClipId || thumbSourceId;
  const thumbSourceRefLabel = thumbClipId ? (thumbClips.find((c) => c.id === thumbClipId)?.name) : thumbSourceAsset?.title;
  const thumbCandidates = thumbSourceRefId ? getThumbnailCandidatesForSource(projectId, thumbSourceKind, thumbSourceRefId) : [];
  const thumbDefaultsForSource = thumbSourceRefId ? (getDefaultThumbnailMap(projectId)[sourceKeyFor(thumbSourceKind, thumbSourceRefId)] || {}) : {};

  const useFrameAsThumbnail = () => {
    if (!capturedFrameUrl || !thumbSourceRefId) return;
    const aspect = aspectForAssetType('thumbnail');
    const frameElement = { id: newId(), type: 'image', x: 0, y: 0, w: 100, h: 100, src: capturedFrameUrl, objectFit: 'cover', isFrameCapture: true };
    const design = {
      id: newId(), name: `${thumbSourceRefLabel || 'Thumbnail'} — Thumbnail ${thumbCandidates.length + 1}`,
      assetType: 'thumbnail', platforms: ['youtube'],
      sourceRef: { kind: thumbSourceKind, id: thumbSourceRefId, projectId, label: thumbSourceRefLabel },
      createdAt: new Date().toISOString(),
      variants: [{ id: newId(), platform: 'youtube', aspectRatio: aspect, approvalStatus: 'draft', output_type: 'thumbnail', brandkit_id: null, pages: [{ id: newId(), background: '#000000', elements: [frameElement] }] }],
    };
    persistDesigns([design, ...designs]);
    setActiveDesignId(design.id); setActiveVariantId(design.variants[0].id); setActivePageId(design.variants[0].pages[0].id); setSelectedElementId(null);
    setCapturing(false); setCapturedFrameUrl(null);
    toast.success('Frame captured — customize it below, then save it as this video\'s thumbnail.');
  };

  const setThumbDefault = (designId, variantId, sourceKind, sourceRefId, sourceLabel) => {
    if (!sourceRefId) return;
    const design = designs.find((d) => d.id === designId);
    const variant = design?.variants.find((v) => v.id === variantId) || design?.variants[0];
    if (!design || !variant) return;
    persistDefaultThumbnail(projectId, sourceKind, sourceRefId, variant.platform, designId, variant.id);
    setThumbDefaultsVersion((v) => v + 1);
    toast.success(`Set as the default ${platformLabel(variant.platform)} thumbnail for "${sourceLabel}"`);
  };

  // Thumbnail Templates (section 5) — reuses the SAME Template Folder /
  // editing_templates[] storage as Image & Post Templates; the frame image
  // is stripped before saving so the template never hard-codes one video's
  // frame (the "critical rule"), and re-applied with the CURRENT capture
  // when the template is used again.
  const buildThumbnailTemplatePayload = (name) => {
    if (!activePage) return {};
    return {
      design: {
        kind: 'thumbnail', aspectRatio: activeVariant?.aspectRatio || '16:9', background: activePage.background,
        brandkit_id: activeVariant?.brandkit_id || null,
        elements: activePage.elements.map((e) => (e.isFrameCapture ? { ...e, src: null } : e)),
      },
    };
  };
  const applyThumbnailTemplate = (tpl, editTpl) => {
    if (!editTpl.design) { toast.error('This template has no reusable thumbnail design saved.'); return; }
    const clonedElements = editTpl.design.elements.map((e) => ({ ...e, id: newId() }));
    const frameEl = clonedElements.find((e) => e.isFrameCapture);
    if (frameEl) frameEl.src = capturedFrameUrl || frameEl.src || null;
    if (!activeDesign || activeDesign.assetType !== 'thumbnail' || activeDesign.sourceRef?.kind !== thumbSourceKind || activeDesign.sourceRef?.id !== thumbSourceRefId) {
      if (!thumbSourceRefId) { toast.error('Pick a video or clip first, in Thumbnails.'); return; }
      const aspect = editTpl.design.aspectRatio || '16:9';
      const design = {
        id: newId(), name: `${thumbSourceRefLabel || 'Thumbnail'} — ${editTpl.name}`, assetType: 'thumbnail', platforms: ['youtube'],
        sourceRef: { kind: thumbSourceKind, id: thumbSourceRefId, projectId, label: thumbSourceRefLabel }, createdAt: new Date().toISOString(),
        variants: [{ id: newId(), platform: 'youtube', aspectRatio: aspect, approvalStatus: 'draft', output_type: 'thumbnail', brandkit_id: editTpl.design.brandkit_id || null, pages: [{ id: newId(), background: editTpl.design.background || '#000000', elements: clonedElements }] }],
      };
      persistDesigns([design, ...designs]);
      setActiveDesignId(design.id); setActiveVariantId(design.variants[0].id); setActivePageId(design.variants[0].pages[0].id); setSelectedElementId(null);
    } else {
      updatePage({ background: editTpl.design.background || activePage.background, elements: clonedElements });
    }
    toast.success(`Applied "${editTpl.name}" — ${frameEl ? 'your captured frame filled the placeholder' : 'capture a frame to fill its image placeholder'}.`);
  };

  // Image & Post Templates (section 8 audit) — the general, non-thumbnail
  // counterpart to the thumbnail pair above: captures every page of the
  // ACTIVE variant (so Carousel/Story multi-page designs round-trip too),
  // not just one page, and always starts a brand-new design rather than
  // mutating whatever is on screen.
  const buildDesignTemplatePayload = () => {
    if (!activeVariant || !activeDesign) return {};
    return {
      design: {
        kind: activeDesign.assetType, aspectRatio: activeVariant.aspectRatio, brandkit_id: activeVariant.brandkit_id || null,
        pages: activeVariant.pages.map((p) => ({ background: p.background, elements: p.elements.map((e) => (e.isFrameCapture ? { ...e, src: null } : e)) })),
      },
    };
  };
  const applyDesignTemplate = (tpl, editTpl) => {
    if (!editTpl.design) { toast.error('This template has no reusable design saved.'); return; }
    const assetType = editTpl.design.kind || 'image_post';
    const pages = editTpl.design.pages.map((p) => ({ id: newId(), background: p.background, elements: p.elements.map((e) => ({ ...e, id: newId() })) }));
    const design = {
      id: newId(), name: `${editTpl.name}`, assetType, platforms: ['instagram'], createdAt: new Date().toISOString(),
      variants: [{ id: newId(), platform: 'instagram', aspectRatio: editTpl.design.aspectRatio || '1:1', approvalStatus: 'draft', output_type: assetType, brandkit_id: editTpl.design.brandkit_id || null, pages }],
    };
    persistDesigns([design, ...designs]);
    setActiveDesignId(design.id); setActiveVariantId(design.variants[0].id); setActivePageId(design.variants[0].pages[0].id); setSelectedElementId(null);
    toast.success(`Applied "${editTpl.name}" as a new design "${design.name}".`);
  };

  const [savingThumbnail, setSavingThumbnail] = useState(false);
  const handleSaveThumbnail = async () => {
    if (!activeDesign || activeDesign.assetType !== 'thumbnail' || !activePage || !activeVariant) return;
    const ref = activeDesign.sourceRef;
    setSavingThumbnail(true);
    try {
      const blob = await renderPageToBlob(activePage, activeVariant.aspectRatio);
      if (ref?.kind === 'video') {
        const formData = new FormData();
        formData.append('file', blob, 'thumbnail.jpg');
        const res = await fetch(`${API_URL}/api/admin/content-sources/${ref.id}/thumbnail`, { method: 'POST', credentials: 'include', body: formData });
        if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Failed to save thumbnail'); }
        toast.success(`Saved — set as the thumbnail for "${ref.label}"`);
      } else if (ref) {
        toast.success('Saved this design. Server-side thumbnails for individual clips aren\'t supported yet (Level 3) — it stays linked to this clip in your project until then.');
      }
      setVariantStatus(activeVariantId, 'approved');
      if (ref) setThumbDefault(activeDesign.id, activeVariantId, ref.kind, ref.id, ref.label);
    } catch (err) {
      toast.error(err.name === 'SecurityError' || err.message === 'toBlob-failed' ? "Couldn't export this design — one of its images may be blocked by cross-origin restrictions." : (err.message || 'Failed to save thumbnail'));
    } finally { setSavingThumbnail(false); }
  };


  // ── Tool sidebar / Properties auto-switch (same pattern as Main Editor & Clips) ──
  const [toolCategory, setToolCategory] = useState('media');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [viewMode, setViewMode] = useState('category');
  const [lastCategoryBeforeProperties, setLastCategoryBeforeProperties] = useState('media');
  const toolCategoryRef = useRef(toolCategory);
  useEffect(() => { toolCategoryRef.current = toolCategory; }, [toolCategory]);
  useEffect(() => {
    if (selectedElementId) {
      setViewMode((vm) => { if (vm !== 'properties') setLastCategoryBeforeProperties(toolCategoryRef.current); return 'properties'; });
    } else {
      setViewMode('category');
    }
  }, [selectedElementId]);
  const handleSelectCategory = (key) => { setViewMode('category'); setToolCategory(key); if (key !== 'thumbnails' && capturing && !capturedFrameUrl) setCapturing(false); };
  const handleBackFromProperties = () => { setViewMode('category'); setToolCategory(lastCategoryBeforeProperties); setSelectedElementId(null); };

  // ── Resizable/collapsible lower workspace (Pages or Layers at a glance) ──
  const [stripHeight, setStripHeight] = useState(180);
  const [stripCollapsed, setStripCollapsed] = useState(false);
  const stripResizeRef = useRef(null);
  const startStripResize = (e) => {
    const startY = e.clientY; const startHeight = stripHeight;
    stripResizeRef.current = { startY, startHeight };
    const onMove = (ev) => { const delta = stripResizeRef.current.startY - ev.clientY; setStripHeight(Math.min(360, Math.max(100, stripResizeRef.current.startHeight + delta))); };
    const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ── Design creation ──
  const createEmptyPage = () => ({ id: newId(), elements: [], background: '#ffffff' });
  const selectDesign = (designId) => {
    const d = designs.find((dd) => dd.id === designId);
    if (!d) return;
    setActiveDesignId(designId);
    setActiveVariantId(d.variants[0]?.id || null);
    setActivePageId(d.variants[0]?.pages[0]?.id || null);
    setSelectedElementId(null);
  };
  const createDesign = (assetType, platformKeys, seedImageUrl = null) => {
    if (!platformKeys.length) { toast.error('Pick at least one supported platform'); return; }
    const aspect = aspectForAssetType(assetType);
    const variants = platformKeys.map((platform) => ({
      id: newId(), platform, aspectRatio: aspect, approvalStatus: 'draft', output_type: assetType, brandkit_id: null,
      pages: [seedImageUrl
        ? { id: newId(), background: '#ffffff', elements: [{ id: newId(), type: 'image', x: 0, y: 0, w: 100, h: 100, src: seedImageUrl, objectFit: 'cover' }] }
        : createEmptyPage()],
    }));
    const design = { id: newId(), name: `${assetTypeLabel(assetType)} ${designs.filter((d) => d.assetType === assetType).length + 1}`, assetType, platforms: platformKeys, createdAt: new Date().toISOString(), variants };
    persistDesigns([design, ...designs]);
    setActiveDesignId(design.id); setActiveVariantId(variants[0].id); setActivePageId(variants[0].pages[0].id); setSelectedElementId(null);
    toast.success(`Created "${design.name}" with ${variants.length} variant${variants.length === 1 ? '' : 's'}`);
  };
  const deleteDesign = (designId) => {
    const next = designs.filter((d) => d.id !== designId);
    persistDesigns(next);
    if (activeDesignId === designId) {
      setActiveDesignId(next[0]?.id || null); setActiveVariantId(next[0]?.variants[0]?.id || null);
      setActivePageId(next[0]?.variants[0]?.pages[0]?.id || null); setSelectedElementId(null);
    }
  };
  const handleStartFromMedia = (assetType, platformKeys) => {
    setPendingCreate({ assetType, platformKeys }); setToolCategory('media');
    toast.info('Pick an image below to start your design from it');
  };
  const handleStartFromTemplate = () => {
    setToolCategory('templates');
    toast.info('Browse an Image & Post Template below, then use Design to start a blank design with that look in mind');
  };

  // ── Media → canvas ──
  const handleQuickAddFromMedia = (asset) => {
    if (!isImageAsset(asset)) { toast.info('Only images can be added to a canvas today — video frame extraction is a Level 3 capability.'); return; }
    if (pendingCreate) { createDesign(pendingCreate.assetType, pendingCreate.platformKeys, asset.input_file_url); setPendingCreate(null); return; }
    if (!activePage) { toast.info('Create a design first, in the Design category.'); setToolCategory('design'); return; }
    addElement({ id: newId(), type: 'image', x: 10, y: 10, w: 80, h: 80, src: asset.input_file_url, objectFit: 'cover' });
  };

  // ── Elements ──
  const addElement = (el) => { if (!activePage) return; updatePage({ elements: [...activePage.elements, el] }); setSelectedElementId(el.id); };
  const addText = (kind) => addElement({
    id: newId(), type: 'text', x: 10, y: kind === 'title' ? 10 : 40, w: 80, h: kind === 'title' ? 18 : 12,
    text: kind === 'title' ? 'Your Title' : 'Body text goes here', fontSize: kind === 'title' ? 36 : 18, color: '#111111', align: 'left', bold: kind === 'title',
  });
  const addShape = (shapeType) => addElement({ id: newId(), type: 'shape', x: 30, y: 30, w: 30, h: shapeType === 'line' ? 2 : 30, shapeType, fill: '#3A7BFF' });
  const updateElement = (elId, partial) => { if (!activePage) return; updatePage({ elements: activePage.elements.map((e) => (e.id === elId ? { ...e, ...partial } : e)) }); };
  const removeElement = (elId) => { if (!activePage) return; updatePage({ elements: activePage.elements.filter((e) => e.id !== elId) }); if (selectedElementId === elId) setSelectedElementId(null); };
  const duplicateElement = (elId) => {
    if (!activePage) return;
    const el = activePage.elements.find((e) => e.id === elId);
    if (!el) return;
    const copy = { ...el, id: newId(), x: Math.min(90, el.x + 3), y: Math.min(90, el.y + 3) };
    updatePage({ elements: [...activePage.elements, copy] }); setSelectedElementId(copy.id);
  };
  const reorderElement = (elId, dir) => {
    if (!activePage) return;
    const idx = activePage.elements.findIndex((e) => e.id === elId);
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= activePage.elements.length) return;
    const next = [...activePage.elements];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    updatePage({ elements: next });
  };
  const toggleElementVisibility = (elId) => {
    if (!activePage) return;
    const el = activePage.elements.find((e) => e.id === elId);
    updateElement(elId, { visible: el?.visible === false });
  };
  const setBackground = (color) => updatePage({ background: color });

  // ── Pages (carousel / multi-image story) ──
  const selectPage = (pageId) => { setActivePageId(pageId); setSelectedElementId(null); };
  const addPage = () => {
    if (!activeVariant) return;
    const p = createEmptyPage();
    updateVariant({ pages: [...activeVariant.pages, p] });
    setActivePageId(p.id); setSelectedElementId(null);
  };
  const duplicatePage = (pageId) => {
    if (!activeVariant) return;
    const p = activeVariant.pages.find((pg) => pg.id === pageId);
    if (!p) return;
    const copy = { ...p, id: newId(), elements: p.elements.map((e) => ({ ...e, id: newId() })) };
    const idx = activeVariant.pages.findIndex((pg) => pg.id === pageId);
    const next = [...activeVariant.pages];
    next.splice(idx + 1, 0, copy);
    updateVariant({ pages: next });
  };
  const removePage = (pageId) => {
    if (!activeVariant || activeVariant.pages.length <= 1) return;
    const next = activeVariant.pages.filter((p) => p.id !== pageId);
    updateVariant({ pages: next });
    if (activePageId === pageId) { setActivePageId(next[0].id); setSelectedElementId(null); }
  };
  const reorderPage = (pageId, dir) => {
    if (!activeVariant) return;
    const idx = activeVariant.pages.findIndex((p) => p.id === pageId);
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= activeVariant.pages.length) return;
    const next = [...activeVariant.pages];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    updateVariant({ pages: next });
  };

  // ── Variants — switching never touches a sibling's own pages/elements/status ──
  const selectVariant = (variantId) => {
    const v = activeDesign?.variants.find((vv) => vv.id === variantId);
    if (!v) return;
    setActiveVariantId(variantId); setActivePageId(v.pages[0]?.id || null); setSelectedElementId(null);
  };
  const applyBrandKit = (brandKitId) => updateVariant({ brandkit_id: brandKitId });
  const applyBrandKitToAllOfDesign = (brandKitId) => {
    if (!activeDesign) return;
    updateDesign(activeDesign.id, { variants: activeDesign.variants.map((v) => ({ ...v, brandkit_id: brandKitId })) });
  };

  // ── Review — local-only status, never claimed as server-persisted ──
  const setVariantStatus = (variantId, status) => {
    if (!activeDesign) return;
    updateDesign(activeDesign.id, { variants: activeDesign.variants.map((v) => (v.id === variantId ? { ...v, approvalStatus: status } : v)) });
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0D12] flex items-center justify-center" data-testid="images-posts-loading">
        <Loader2 className="h-6 w-6 text-[#3A7BFF] animate-spin" />
      </div>
    );
  }
  if (notFound || !project) {
    return (
      <div className="min-h-screen bg-[#0B0D12] text-white flex items-center justify-center flex-col gap-3" data-testid="images-posts-not-found">
        <p className="text-sm text-[#8A93A6]">This project could not be found.</p>
        <Link to="/app/create/workspace" className="text-sm text-[#3A7BFF] hover:underline">Back to Workspace</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0D12] text-white pb-10" data-testid="images-posts-workspace">
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleThumbFileChange} data-testid="images-posts-thumb-file-input" />
      <div className="container mx-auto p-6 max-w-[1600px]">
        <div className="flex flex-wrap items-center justify-between gap-y-2 mb-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs text-[#8A93A6] mb-2">
              <Link to="/app/create" className="hover:text-white">Create</Link><span className="opacity-50">/</span>
              <Link to="/app/create/workspace" className="hover:text-white">Workspace</Link><span className="opacity-50">/</span>
              <Link to={returnTo} className="hover:text-white" data-testid="images-posts-back-to-project-link">{project?.name || 'Project'}</Link>
              <span className="opacity-50">/</span>
              <span className="text-[#C0C7D4]">Images &amp; Posts</span>
            </div>
            <h1 className="text-2xl font-bold truncate" data-testid="images-posts-title">{activeDesign?.name || 'Images & Posts'}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="border-white/10" onClick={() => navigate(returnTo)} data-testid="images-posts-back-button">
              <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />Back to Project
            </Button>
            <ProductionToolsDropdown currentTool="images-posts" creatorId={project?.creator_id} projectId={projectId} returnTo={returnTo} />
          </div>
        </div>        <div className="space-y-3" data-testid="images-posts-shell">
          <div className="flex gap-3 items-stretch">
            <EditorToolSidebar
              categories={DESIGN_TOOL_CATEGORIES}
              activeCategory={viewMode === 'properties' ? null : toolCategory}
              onSelectCategory={handleSelectCategory}
              collapsed={sidebarCollapsed}
              onToggleCollapsed={() => setSidebarCollapsed((c) => !c)}
            />

            {!sidebarCollapsed && (
              <div className="w-72 shrink-0 lg:h-[480px] overflow-y-auto" data-testid="images-posts-tool-panel">
                {viewMode === 'properties' && selectedElement ? (
                  <div className="glass rounded-xl p-3 lg:h-[480px] flex flex-col" data-testid="design-properties-view">
                    <button onClick={handleBackFromProperties} className="flex items-center gap-1.5 text-xs text-[#8A93A6] hover:text-white mb-2 shrink-0" data-testid="design-properties-back-button">
                      <ArrowLeft className="h-3.5 w-3.5" />Back to {CATEGORY_LABELS[lastCategoryBeforeProperties] || 'tools'}
                    </button>
                    <div className="overflow-y-auto flex-1 pr-1">
                      <DesignElementProperties
                        element={selectedElement}
                        onChange={(p) => updateElement(selectedElement.id, p)}
                        onDelete={() => removeElement(selectedElement.id)}
                        onDuplicate={() => duplicateElement(selectedElement.id)}
                        onBringForward={() => reorderElement(selectedElement.id, 1)}
                        onSendBackward={() => reorderElement(selectedElement.id, -1)}
                        onToggleVisibility={() => toggleElementVisibility(selectedElement.id)}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="glass rounded-xl p-4 lg:h-[480px] overflow-y-auto" data-testid={`images-posts-panel-${toolCategory}`}>
                    {toolCategory === 'media' && (
                      <div className="space-y-4">
                        <EditorMediaPanel
                          sources={parentAssets} parentAssetId={null}
                          onQuickAdd={handleQuickAddFromMedia} context="design"
                          uploadTo={`${returnTo}/upload`} libraryTo={`/app/create/content-studio?projectId=${projectId}&returnTo=${encodeURIComponent('/app/create/workspace/project/' + projectId + '/images-posts')}`}
                          startFromVideos={[]} startFromLoading={false} onStartFromVideo={undefined}
                        />
                        {imageAssets.length > 0 && (
                          <div className="pt-3 border-t border-white/10">
                            <p className="text-[11px] uppercase tracking-wide text-[#555E6E] mb-1.5">Project Thumbnails</p>
                            <div className="grid grid-cols-3 gap-1.5">
                              {imageAssets.map((a) => (
                                <button key={a.id} onClick={() => triggerSetThumbnail(a.id)} className="relative aspect-square rounded-md overflow-hidden border border-white/10 bg-black/40 flex items-center justify-center" title="Set as project thumbnail" data-testid={`images-posts-set-thumbnail-${a.id}`}>
                                  {thumbMap[a.id] ? <img src={thumbMap[a.id]} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="h-4 w-4 text-[#555E6E]" />}
                                  {uploadingId === a.id && <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><Loader2 className="h-3 w-3 animate-spin text-white" /></div>}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {toolCategory === 'design' && (
                      <DesignCreatePanel
                        designs={designs.filter((d) => d.assetType !== 'thumbnail')} activeDesignId={activeDesignId} onSelectDesign={selectDesign}
                        onCreateDesign={createDesign} onDeleteDesign={deleteDesign}
                        onStartFromMedia={handleStartFromMedia} onStartFromTemplate={handleStartFromTemplate}
                      />
                    )}

                    {toolCategory === 'thumbnails' && (
                      <ThumbnailSourcePanel
                        sources={parentAssets} selectedSourceId={thumbSourceId} onSelectSource={selectThumbSource}
                        clips={thumbClips} clipsLoading={thumbClipsLoading} selectedClipId={thumbClipId} onSelectClip={selectThumbClip}
                        candidates={thumbCandidates} activeDesignId={activeDesignId} onSelectCandidate={selectDesign}
                        onCaptureNew={startCapture} isCapturing={capturing}
                        defaultsForSource={thumbDefaultsForSource} onSetDefault={(designId, variantId) => setThumbDefault(designId, variantId, thumbSourceKind, thumbSourceRefId, thumbSourceRefLabel)}
                      />
                    )}

                    {toolCategory === 'text' && <DesignTextPanel onAddText={addText} disabled={!activePage} />}
                    {toolCategory === 'elements' && <DesignElementsPalette onAddShape={addShape} background={activePage?.background || '#ffffff'} onBackgroundChange={setBackground} disabled={!activePage} />}

                    {toolCategory === 'ai' && (
                      <div className="space-y-2" data-testid="design-ai-tools-panel">
                        <p className="text-[11px] text-[#8A93A6] mb-1">Existing AI capabilities for this canvas — none are connected to a provider yet, so requests will not complete today.</p>
                        {AI_CAPABILITIES.map((c) => (
                          <div key={c.key} className="flex items-start gap-2 p-2.5 rounded-lg border border-white/10 bg-white/[0.02]" data-testid={`design-ai-capability-${c.key}`}>
                            <c.icon className="h-4 w-4 text-[#3A7BFF] mt-0.5 shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className="text-xs text-white">{c.label}</p>
                              <p className="text-[10px] text-[#8A93A6]">{c.desc}</p>
                            </div>
                            <Button size="sm" variant="outline" disabled className="h-6 text-[10px] border-white/10 text-[#555E6E] shrink-0" data-testid={`design-ai-request-${c.key}`}>Not connected</Button>
                          </div>
                        ))}
                      </div>
                    )}

                    {toolCategory === 'brand' && (
                      <EditorBrandKitPanel
                        clip={activeVariant ? { brandkit_id: activeVariant.brandkit_id, output_type: activeDesign.assetType } : null}
                        brandkits={brandkits} defaultBrandKitId={brandkits[0]?.id}
                        onApply={applyBrandKit} onApplyToAllOfType={applyBrandKitToAllOfDesign} nounLabel="variants"
                      />
                    )}

                    {toolCategory === 'templates' && (
                      activeDesign?.assetType === 'thumbnail' ? (
                        <EditorTemplatesPanel
                          creatorId={project?.creator_id} templateKind="Thumbnail Template"
                          filterTemplate={(et) => et.design?.kind === 'thumbnail'}
                          buildTemplatePayload={buildThumbnailTemplatePayload}
                          onApply={applyThumbnailTemplate}
                          applyHint="Capture a frame in Thumbnails, customize it, then save it here as a reusable Thumbnail Template."
                          projectId={projectId} episodeId={thumbSourceRefId}
                        />
                      ) : (
                        <EditorTemplatesPanel
                          creatorId={project?.creator_id} templateKind="Image & Post Template"
                          filterTemplate={(et) => !et.design || et.design.kind !== 'thumbnail'}
                          buildTemplatePayload={buildDesignTemplatePayload}
                          onApply={applyDesignTemplate}
                          applyHint="Create a design above, then save it here as a reusable Image & Post Template."
                          projectId={projectId} episodeId={activeDesign?.id}
                        />
                      )
                    )}

                    {toolCategory === 'layers' && (
                      activeVariant ? (
                        <DesignLayersPages
                          pages={activeVariant.pages} activePageId={activePageId} onSelectPage={selectPage}
                          onAddPage={addPage} onDuplicatePage={duplicatePage} onRemovePage={removePage} onReorderPage={reorderPage}
                          elements={activePage?.elements || []} onSelectElement={setSelectedElementId} selectedElementId={selectedElementId}
                          onReorderElement={reorderElement} onRemoveElement={removeElement} onToggleVisibility={toggleElementVisibility}
                          multiPage={multiPage}
                        />
                      ) : <p className="text-sm text-[#8A93A6] text-center py-6" data-testid="design-layers-pages-empty">Create or select a design first.</p>
                    )}

                    {toolCategory === 'review' && (
                      <DesignReviewPanel
                        variants={activeDesign?.variants || []} activeVariantId={activeVariantId}
                        onSelectVariant={selectVariant} onCycleStatus={setVariantStatus}
                        onReject={(vid) => setVariantStatus(vid, 'rejected')}
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="glass rounded-xl p-3 lg:h-[480px] flex flex-col" data-testid="design-preview-area">
                {capturing ? (
                  <ThumbnailCaptureStage
                    streamUrl={thumbStreamUrl} streamError={thumbStreamError} clipRange={thumbClipRange} videoRef={thumbVideoRef}
                    sourceSelected={!!thumbSourceId}
                    currentTime={thumbCurrentTime} duration={thumbDuration} isPlaying={thumbIsPlaying}
                    onTogglePlay={thumbTogglePlay} onSeek={thumbSeek} onStepFrame={thumbStepFrame}
                    capturedFrameUrl={capturedFrameUrl} captureError={captureError}
                    onCapture={handleFrameCaptured} onRetake={retakeCapture} onUseFrame={useFrameAsThumbnail}
                  />
                ) : !activeDesign ? (
                  <div className="flex-1 flex flex-col items-center justify-center text-center gap-3" data-testid="design-no-design-prompt">
                    <PenTool className="h-8 w-8 text-[#555E6E]" />
                    <p className="text-sm text-[#8A93A6] max-w-xs">Start a new design from Design on the left — blank, from an uploaded image, or from a template. Every other tool stays browseable in the meantime.</p>
                    <Button size="sm" className="bg-[#3A7BFF] hover:bg-[#3A7BFF]/90" onClick={() => handleSelectCategory('design')} data-testid="design-no-design-cta">
                      <PenTool className="h-3.5 w-3.5 mr-1.5" />Open Design
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between pb-2 shrink-0">
                      <div className="flex items-center gap-1.5">
                        <Button size="sm" variant="outline" className="h-7 border-white/10 text-xs" disabled={!selectedElement} onClick={() => duplicateElement(selectedElement.id)} data-testid="design-toolbar-duplicate"><Copy className="h-3 w-3 mr-1" />Duplicate</Button>
                        <Button size="sm" variant="outline" className="h-7 border-white/10 text-xs" disabled={!selectedElement} onClick={() => reorderElement(selectedElement.id, 1)} data-testid="design-toolbar-forward"><ChevronUp className="h-3 w-3" /></Button>
                        <Button size="sm" variant="outline" className="h-7 border-white/10 text-xs" disabled={!selectedElement} onClick={() => reorderElement(selectedElement.id, -1)} data-testid="design-toolbar-backward"><ChevronDown className="h-3 w-3" /></Button>
                        <Button size="sm" variant="outline" className="h-7 border-red-500/30 text-red-400 text-xs" disabled={!selectedElement} onClick={() => removeElement(selectedElement.id)} data-testid="design-toolbar-delete"><Trash2 className="h-3 w-3 mr-1" />Delete</Button>
                        {activeDesign.assetType === 'thumbnail' && (
                          <Button size="sm" className="h-7 bg-[#3A7BFF] hover:bg-[#3A7BFF]/90 text-xs ml-1" disabled={savingThumbnail} onClick={handleSaveThumbnail} data-testid="design-toolbar-save-thumbnail">
                            {savingThumbnail ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Camera className="h-3 w-3 mr-1" />}
                            {activeDesign.sourceRef?.kind === 'video' ? 'Save Thumbnail' : 'Save for this Clip'}
                          </Button>
                        )}
                      </div>
                      <span className="text-[11px] text-[#8A93A6] bg-white/5 px-2 py-1 rounded-md" data-testid="design-toolbar-aspect-badge">{activeVariant?.aspectRatio} · {platformLabel(activeVariant?.platform)}</span>
                    </div>
                    <div className="flex-1 min-h-0">
                      <DesignCanvas
                        page={activePage} aspectRatio={activeVariant?.aspectRatio}
                        selectedElementId={selectedElementId} onSelectElement={setSelectedElementId}
                        onUpdateElement={updateElement} onDeselect={() => setSelectedElementId(null)}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {activeDesign && activeDesign.variants.length > 1 && (
            <div className="flex items-center gap-2 flex-wrap px-1" data-testid="design-variant-switcher">
              <span className="text-[11px] text-[#8A93A6] flex items-center gap-1"><Layers className="h-3 w-3" />Platform variants:</span>
              {activeDesign.variants.map((v) => (
                <button
                  key={v.id} onClick={() => selectVariant(v.id)}
                  className={`text-[11px] px-2 py-1 rounded-full border ${v.id === activeVariantId ? 'border-[#3A7BFF] bg-[#3A7BFF]/15 text-white' : 'border-white/10 text-[#8A93A6] hover:text-white'}`}
                  data-testid={`design-variant-switch-${v.id}`}
                >
                  {platformLabel(v.platform)}
                </button>
              ))}
              <span className="text-[10px] text-[#555E6E]">Each variant keeps its own layout — editing one never overwrites another.</span>
            </div>
          )}

          <div className="relative">
            <div
              className="h-2 -mb-1 flex items-center justify-center gap-2 cursor-row-resize group relative z-10"
              onMouseDown={stripCollapsed ? undefined : startStripResize}
              data-testid="design-strip-resize-handle"
              title={stripCollapsed ? 'Strip collapsed' : 'Drag to resize'}
            >
              <div className="h-1 w-16 rounded-full bg-white/10 group-hover:bg-[#3A7BFF]/60 transition-colors" />
              <button
                type="button" onClick={(e) => { e.stopPropagation(); setStripCollapsed((c) => !c); }}
                className="absolute right-1 -top-1 h-5 w-5 rounded-md bg-white/5 hover:bg-white/10 text-[#8A93A6] hover:text-white flex items-center justify-center"
                data-testid="design-strip-collapse-toggle"
              >
                {stripCollapsed ? <ChevronsUp className="h-3.5 w-3.5" /> : <ChevronsDown className="h-3.5 w-3.5" />}
              </button>
            </div>
            <div style={stripCollapsed ? { display: 'none' } : { height: stripHeight }} className="glass rounded-xl p-3 overflow-hidden flex flex-col" data-testid="design-strip">
              {!activeVariant ? (
                <p className="text-sm text-[#8A93A6] m-auto" data-testid="design-strip-empty">Create a design to see its pages and layers here.</p>
              ) : multiPage ? (
                <>
                  <p className="text-[11px] text-[#8A93A6] mb-2 shrink-0">{activeVariant.pages.length} page{activeVariant.pages.length === 1 ? '' : 's'} in this variant</p>
                  <div className="flex-1 overflow-x-auto flex gap-2 pb-1">
                    {activeVariant.pages.map((p, idx) => (
                      <button key={p.id} onClick={() => selectPage(p.id)} className={`shrink-0 w-28 h-full rounded-lg border flex flex-col items-center justify-center gap-1 ${p.id === activePageId ? 'border-[#3A7BFF] bg-[#3A7BFF]/10' : 'border-white/10 hover:bg-white/5'}`} data-testid={`design-strip-page-${p.id}`}>
                        <span className="text-xs text-white">Page {idx + 1}</span>
                        <span className="text-[10px] text-[#8A93A6]">{p.elements.length} object{p.elements.length === 1 ? '' : 's'}</span>
                      </button>
                    ))}
                    <button onClick={addPage} className="shrink-0 w-28 h-full rounded-lg border border-dashed border-white/15 text-[#8A93A6] hover:text-white hover:border-white/30 flex flex-col items-center justify-center gap-1" data-testid="design-strip-add-page">
                      <span className="text-lg">+</span><span className="text-[10px]">Add Page</span>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-[11px] text-[#8A93A6] mb-2 shrink-0">{(activePage?.elements || []).length} layer{(activePage?.elements || []).length === 1 ? '' : 's'} on this page</p>
                  <div className="flex-1 overflow-x-auto flex gap-2 pb-1">
                    {(activePage?.elements || []).length === 0 ? (
                      <p className="text-sm text-[#8A93A6] m-auto">Add text, an image or a shape from the left panel.</p>
                    ) : [...activePage.elements].reverse().map((el) => (
                      <button key={el.id} onClick={() => setSelectedElementId(el.id)} className={`shrink-0 w-28 h-full rounded-lg border flex flex-col items-center justify-center gap-1 ${el.id === selectedElementId ? 'border-[#3A7BFF] bg-[#3A7BFF]/10' : 'border-white/10 hover:bg-white/5'}`} data-testid={`design-strip-layer-${el.id}`}>
                        <span className="text-xs text-white capitalize">{el.type}</span>
                        {el.type === 'text' && <span className="text-[10px] text-[#8A93A6] truncate max-w-full px-1">{el.text}</span>}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
